import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Alert,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { Text, TextInput, Button, Surface } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { RootStackParamList } from '../types/navigation';
import { CreateResourceRequest } from '../types/resource';
import resourceService from '../services/resourceService';
import MaxWidthContainer from '../components/MaxWidthContainer';
import HeaderBar from '../components/HeaderBar';
import SlidingMenu from '../components/SlidingMenu';
import { brandPrimary, brandPrimarySoft, brandButton } from '../theme/paperTheme';

type AddResourceScreenNavigationProp = NativeStackNavigationProp<RootStackParamList, 'AddResource'>;

const inputOutline = {
  outlineColor: 'rgba(15, 23, 42, 0.12)',
  activeOutlineColor: brandPrimary,
};

const AddResourceScreen: React.FC = () => {
  const navigation = useNavigation<AddResourceScreenNavigationProp>();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'document' | 'link' | 'video'>('link');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMenuVisible, setIsMenuVisible] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim()) {
      Alert.alert('Error', 'Please enter a title');
      return;
    }

    if ((type === 'link' || type === 'video') && !url.trim()) {
      Alert.alert('Error', 'Please enter a URL');
      return;
    }

    if ((type === 'link' || type === 'video') && url.trim()) {
      const urlPattern = /^https?:\/\/.+/;
      if (!urlPattern.test(url.trim())) {
        Alert.alert('Error', 'Please enter a valid URL (starting with http:// or https://)');
        return;
      }
    }

    try {
      setLoading(true);

      const resourceData: CreateResourceRequest = {
        title: title.trim(),
        description: description.trim(),
        type,
        url: type === 'link' || type === 'video' ? url.trim() : undefined,
      };

      await resourceService.createResource(resourceData);
      Alert.alert('Success', 'Resource created successfully!');
      navigation.goBack();
    } catch (error) {
      console.error('Error creating resource:', error);
      Alert.alert('Error', 'Failed to create resource');
    } finally {
      setLoading(false);
    }
  };

  const renderTypeOption = (
    optionType: 'document' | 'link' | 'video',
    icon: keyof typeof Ionicons.glyphMap,
    label: string,
  ) => {
    const selected = type === optionType;
    return (
      <Pressable
        style={[styles.typeOption, selected && styles.typeOptionSelected]}
        onPress={() => setType(optionType)}
      >
        <Ionicons name={icon} size={22} color={selected ? '#FFFFFF' : '#64748B'} />
        <Text style={[styles.typeLabel, selected && styles.typeLabelSelected]}>{label}</Text>
      </Pressable>
    );
  };

  const toggleMenu = () => setIsMenuVisible((v) => !v);

  return (
    <View style={styles.container}>
      <Surface style={styles.surface}>
        <View style={styles.headerWrap}>
          <HeaderBar
            rightContent={
              <TouchableOpacity style={styles.menuButton} onPress={toggleMenu}>
                <Text style={styles.menuText}>☰</Text>
              </TouchableOpacity>
            }
            onLogoPress={() => navigation.navigate('Home')}
            style={styles.headerBar}
          />
        </View>

        <MaxWidthContainer style={isDesktop ? styles.wideContainer : undefined}>
          <KeyboardAvoidingView
            style={styles.flex}
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          >
            <ScrollView
              contentContainerStyle={styles.content}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
            >
              <Text style={styles.screenLabel}>Resources</Text>
              <Text style={styles.screenTitle}>Add a resource</Text>
              <Text style={styles.subtitle}>
                Share a link, video, or document with the community.
              </Text>

              <Surface style={styles.formCard} elevation={0}>
                <Text style={styles.sectionTitle}>Resource type</Text>
                <View style={styles.typeContainer}>
                  {renderTypeOption('link', 'link-outline', 'Link')}
                  {renderTypeOption('video', 'videocam-outline', 'Video')}
                  {renderTypeOption('document', 'document-text-outline', 'Document')}
                </View>

                <TextInput
                  mode="outlined"
                  label="Title *"
                  value={title}
                  onChangeText={setTitle}
                  style={styles.input}
                  maxLength={100}
                  {...inputOutline}
                />

                <TextInput
                  mode="outlined"
                  label="Description"
                  value={description}
                  onChangeText={setDescription}
                  style={[styles.input, styles.textArea]}
                  multiline
                  numberOfLines={4}
                  maxLength={500}
                  {...inputOutline}
                />

                {type === 'link' || type === 'video' ? (
                  <TextInput
                    mode="outlined"
                    label="URL *"
                    value={url}
                    onChangeText={setUrl}
                    style={styles.input}
                    keyboardType="url"
                    autoCapitalize="none"
                    autoCorrect={false}
                    placeholder={`Enter ${type} URL`}
                    {...inputOutline}
                  />
                ) : null}

                {type === 'document' ? (
                  <View style={styles.infoContainer}>
                    <Ionicons name="information-circle-outline" size={22} color={brandPrimary} />
                    <Text style={styles.infoText}>
                      Documents can be uploaded from the main Resources screen using the Upload PDF
                      button.
                    </Text>
                  </View>
                ) : null}

                <Button
                  mode="contained"
                  onPress={handleSubmit}
                  loading={loading}
                  disabled={loading}
                  style={styles.submitButton}
                  buttonColor={brandButton}
                  textColor="#ffffff"
                >
                  Create resource
                </Button>

                <Button
                  mode="text"
                  onPress={() => navigation.goBack()}
                  style={styles.cancelButton}
                  textColor={brandPrimary}
                >
                  Cancel
                </Button>
              </Surface>
            </ScrollView>
          </KeyboardAvoidingView>
        </MaxWidthContainer>
      </Surface>

      <SlidingMenu isVisible={isMenuVisible} onClose={() => setIsMenuVisible(false)} />
    </View>
  );
};

const cardShadow = Platform.OS === 'web'
  ? { boxShadow: '0 4px 20px rgba(15, 23, 42, 0.06)' }
  : {
      shadowColor: '#0F172A',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.06,
      shadowRadius: 12,
      elevation: 3,
    };

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F4F7FB',
  },
  surface: {
    flex: 1,
    elevation: 0,
    backgroundColor: '#F4F7FB',
  },
  flex: {
    flex: 1,
  },
  wideContainer: {
    maxWidth: 960,
  },
  headerWrap: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 0 rgba(15, 23, 42, 0.06)' }
      : {
          borderBottomWidth: 1,
          borderBottomColor: 'rgba(15, 23, 42, 0.06)',
        }),
  },
  headerBar: {
    marginHorizontal: 8,
    marginBottom: 0,
  },
  menuButton: {
    width: 52,
    height: 52,
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuText: {
    fontSize: 36,
    color: '#1E293B',
  },
  content: {
    paddingHorizontal: 20,
    paddingTop: 28,
    paddingBottom: 48,
  },
  screenLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: brandPrimary,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 8,
  },
  screenTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: '#1E293B',
    letterSpacing: -0.5,
    marginBottom: 10,
    lineHeight: 36,
  },
  subtitle: {
    fontSize: 16,
    color: '#64748B',
    lineHeight: 24,
    marginBottom: 24,
  },
  formCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    padding: 24,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    ...cardShadow,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#475569',
    marginBottom: 12,
  },
  typeContainer: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 20,
    backgroundColor: '#FFFFFF',
    borderRadius: 999,
    padding: 4,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.08)',
  },
  typeOption: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 10,
    borderRadius: 999,
    gap: 4,
  },
  typeOptionSelected: {
    backgroundColor: brandButton,
  },
  typeLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#64748B',
  },
  typeLabelSelected: {
    color: '#FFFFFF',
  },
  input: {
    marginBottom: 12,
    backgroundColor: '#FFFFFF',
  },
  textArea: {
    minHeight: 100,
  },
  infoContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: brandPrimarySoft,
    padding: 14,
    borderRadius: 12,
    gap: 10,
    marginBottom: 12,
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    color: '#0F766E',
    lineHeight: 20,
  },
  submitButton: {
    marginTop: 8,
    borderRadius: 999,
    paddingVertical: 4,
  },
  cancelButton: {
    marginTop: 4,
  },
});

export default AddResourceScreen;
